"use client"

import type React from "react"
import { useState, useEffect } from "react"
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonLoading,
  IonToast,
  IonButtons,
  IonBackButton,
  IonSegment,
  IonSegmentButton,
  IonLabel,
} from "@ionic/react"
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  IconButton,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Badge,
  Tooltip,
  InputAdornment,
  TablePagination,
} from "@mui/material"
import {
  Check,
  Close,
  Info,
  FilterList,
  Visibility,
  ThumbUp,
  ThumbDown,
  Search,
  Refresh,
  CheckCircle,
  Pending,
  Cancel,
} from "@mui/icons-material"
import { styled } from "@mui/material/styles"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"

import { getDepartmentRequests, updateRequestStatus, getDepartmentsList } from "../../api/accounting/accounting"

const StyledCard = styled(Card)(({ theme }) => ({
  margin: theme.spacing(2),
  padding: theme.spacing(3),
  borderRadius: 12,
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
  backgroundColor: "#ffffff",
  border: "1px solid #e8edf2",
  [theme.breakpoints.down("sm")]: {
    margin: theme.spacing(1),
    padding: theme.spacing(2),
  },
}))

const PageContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: "#f5f8fa",
  minHeight: "100vh",
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1),
  },
}))

const StyledHeader = styled(Box)(({ theme }) => ({
  background: "linear-gradient(135deg, #0066cc 0%, #0052a3 100%)",
  padding: theme.spacing(2, 3),
  borderRadius: "12px 12px 0 0",
  marginBottom: theme.spacing(2),
}))

const StatsCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: 12,
  border: "1px solid #e8edf2",
  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
  transition: "transform 0.2s, box-shadow 0.2s",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
  },
}))

interface RequestItem {
  id: string
  item_id: string
  stock_name: string
  requested_qty: number
  approved_qty?: number
  unit_price: string
  available_quantity: string
  status?: string
}

interface DepartmentRequest {
  reference: string
  department_id: string
  department_name: string
  requested_by: string
  requested_by_name?: string // Assuming this might be available or needed
  status: string
  total_value: string
  created_at: string
  updated_at: string
  notes?: string
  items: RequestItem[]
}

const ItemTransferPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<"pending" | "all">("pending")
  const [requests, setRequests] = useState<DepartmentRequest[]>([])
  const [filteredRequests, setFilteredRequests] = useState<DepartmentRequest[]>([])
  const [loading, setLoading] = useState(false)
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState("")
  const [toastType, setToastType] = useState<"success" | "error">("success")
  const [selectedRequest, setSelectedRequest] = useState<DepartmentRequest | null>(null)
  const [showDetailDialog, setShowDetailDialog] = useState(false)
  const [showApproveDialog, setShowApproveDialog] = useState(false)
  const [showRejectDialog, setShowRejectDialog] = useState(false)
  const [selectedAction, setSelectedAction] = useState<"approve" | "reject" | null>(null)
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null)

  // Filter states
  const [departments, setDepartments] = useState<any[]>([])
  const [selectedDepartment, setSelectedDepartment] = useState<string>("all")
  const [statusFilter, setStatusFilter] = useState<string>("all")
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)
  const [searchQuery, setSearchQuery] = useState("")

  // Pagination
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [totalRequests, setTotalRequests] = useState(0)

  const statusOptions = [
    { value: "all", label: "All Statuses" },
    { value: "pending", label: "Pending" },
    { value: "approved", label: "Approved" },
    { value: "partially_approved", label: "Partially Approved" },
    { value: "rejected", label: "Rejected" },
    { value: "delivered", label: "Delivered" },
  ]

  useEffect(() => {
    fetchDepartments()
    fetchRequests()
  }, [activeTab, page, rowsPerPage])

  useEffect(() => {
    // When on pending tab, show all fetched pending requests without additional filtering
    if (activeTab === "pending") {
      setFilteredRequests(requests)
    } else {
      applyFilters()
    }
  }, [requests, selectedDepartment, statusFilter, startDate, endDate, searchQuery, activeTab])

  const fetchDepartments = async () => {
    try {
      const response = await getDepartmentsList()
      console.log(response)
      if (response.data && Array.isArray(response.data)) {
        setDepartments(response.data)
      }
    } catch (error) {
      console.error("Failed to fetch departments:", error)
    }
  }

  const fetchRequests = async () => {
    setLoading(true)
    try {
      const filters: any = {
        page: page + 1,
        per_page: rowsPerPage,
      }

      if (activeTab === "pending") {
        filters.status = "pending"
      } else {
        // Apply all filters only on 'all' tab
        if (selectedDepartment !== "all") {
          filters.department_id = selectedDepartment
        }
        if (statusFilter !== "all") {
          filters.status = statusFilter
        }
        if (startDate) {
          filters.start_date = startDate.toISOString().split("T")[0]
        }
        if (endDate) {
          filters.end_date = endDate.toISOString().split("T")[0]
        }
        if (searchQuery) {
          filters.search = searchQuery
        }
      }

      const response = await getDepartmentRequests(filters)
      console.log(response)
      if (response.data && response.data.data) {
        setRequests(response.data.data)
        setTotalRequests(response.data.total || response.data.data.length)
      }
    } catch (error) {
      showErrorToast("Failed to fetch requests")
    } finally {
      setLoading(false)
    }
  }

  const applyFilters = () => {
    let filtered = [...requests]

    if (selectedDepartment !== "all") {
      filtered = filtered.filter((req) => req.department_id === selectedDepartment)
    }

    if (statusFilter !== "all") {
      filtered = filtered.filter((req) => req.status === statusFilter)
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(
        (req) =>
          req.department_name.toLowerCase().includes(query) ||
          req.reference.toLowerCase().includes(query) ||
          req.requested_by.toLowerCase().includes(query),
      )
    }

    setFilteredRequests(filtered)
  }

  const handleViewRequest = (request: DepartmentRequest) => {
    setSelectedRequest(request)
    setShowDetailDialog(true)
  }

  const handleApproveRequest = (requestId: string) => {
    setSelectedRequestId(requestId)
    setSelectedAction("approve")
    setShowApproveDialog(true)
  }

  const handleRejectRequest = (requestId: string) => {
    setSelectedRequestId(requestId)
    setSelectedAction("reject")
    setShowRejectDialog(true)
  }

  const handleUpdateItemQuantity = (itemId: string, newQuantity: number) => {
    if (!selectedRequest) return

    const updatedItems = selectedRequest.items.map((item) =>
      item.id === itemId ? { ...item, approved_qty: newQuantity } : item,
    )

    setSelectedRequest({
      ...selectedRequest,
      items: updatedItems,
    })
  }

  const handleSubmitApproval = async () => {
    if (!selectedRequest) return

    setLoading(true)
    try {
      const requestData = {
        reference: selectedRequest.reference,
        status: "approved",
        items: selectedRequest.items.map((item) => ({
          item_id: item.item_id,
          approved_qty: item.approved_qty || item.requested_qty,
          notes: item.status || "", // This might need clarification, assuming item status refers to notes
        })),
      }

      const response = await updateRequestStatus(requestData)
      console.log({ response })
      showSuccessToast("Request approved successfully!")
      setShowApproveDialog(false)
      setSelectedRequest(null)
      fetchRequests() // Re-fetch requests to update the list
    } catch (error) {
      showErrorToast("Failed to approve request")
    } finally {
      setLoading(false)
    }
  }

  const handleSubmitRejection = async (rejectionReason: string) => {
    if (!selectedRequestId) return

    setLoading(true)
    try {
      const requestData = {
        reference: selectedRequestId,
        status: "rejected",
        rejection_reason: rejectionReason,
      }

      await updateRequestStatus(requestData)
      showSuccessToast("Request rejected successfully!")
      setShowRejectDialog(false)
      setSelectedRequestId(null)
      fetchRequests() // Re-fetch requests to update the list
    } catch (error) {
      showErrorToast("Failed to reject request")
    } finally {
      setLoading(false)
    }
  }

  const getStatusChip = (status: string) => {
    const statusMap: any = {
      pending: { label: "Pending Review", color: "#FFA726", bgColor: "#FFF3E0", icon: <Pending /> },
      approved: { label: "Approved", color: "#66BB6A", bgColor: "#E8F5E9", icon: <CheckCircle /> },
      partially_approved: { label: "Partially Approved", color: "#42A5F5", bgColor: "#E3F2FD", icon: <CheckCircle /> },
      rejected: { label: "Rejected", color: "#EF5350", bgColor: "#FFEBEE", icon: <Cancel /> },
      delivered: { label: "Delivered", color: "#26A69A", bgColor: "#E0F2F1", icon: <CheckCircle /> },
    }

    const statusInfo = statusMap[status] || { label: status, color: "#757575", bgColor: "#F5F5F5", icon: null }

    return (
      <Chip
        label={statusInfo.label}
        size="small"
        icon={statusInfo.icon}
        sx={{
          backgroundColor: statusInfo.bgColor,
          color: statusInfo.color,
          fontWeight: 600,
          border: `1px solid ${statusInfo.color}40`,
          fontSize: "0.75rem",
          height: "26px",
        }}
      />
    )
  }

  const showSuccessToast = (message: string) => {
    setToastMessage(message)
    setToastType("success")
    setShowToast(true)
  }

  const showErrorToast = (message: string) => {
    setToastMessage(message)
    setToastType("error")
    setShowToast(true)
  }


  const getPendingCount = () => {
    // Ensure requests is not empty before filtering
    if (!requests) return 0
    return requests.filter((req) => req.status == "pending").length
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar style={{ "--background": "#0066cc" }}>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/dashboard" style={{ "--color": "#ffffff" }} />
          </IonButtons>
          <IonTitle style={{ color: "#ffffff", fontWeight: 600 }}>Hospital Inventory Management</IonTitle>
        </IonToolbar>
        <IonToolbar style={{ "--background": "#0052a3" }}>
          <IonSegment
            value={activeTab}
            onIonChange={(e) => {
              setActiveTab(e.detail.value as any)
              setPage(0)
              setSelectedDepartment("all")
              setStatusFilter("all")
              setSearchQuery("")
              setStartDate(null)
              setEndDate(null)
            }}
            style={{
              width: "100%",
              "--background": "transparent",
            }}
          >
            <IonSegmentButton value="pending" style={{ "--indicator-color": "#ffffff" }}>
              <IonLabel>
                <Badge
                  badgeContent={getPendingCount()}
                  sx={{
                    "& .MuiBadge-badge": {
                      backgroundColor: "#ff6b6b",
                      color: "#ffffff",
                      fontWeight: 700,
                    },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "#ffffff" }}>
                    <Pending fontSize="small" />
                    <Typography variant="body2" fontWeight={600}>
                      Pending Requests
                    </Typography>
                  </Box>
                </Badge>
              </IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="all" style={{ "--indicator-color": "#ffffff" }}>
              <IonLabel>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "#ffffff" }}>
                  <FilterList fontSize="small" />
                  <Typography variant="body2" fontWeight={600}>
                    All Requests
                  </Typography>
                </Box>
              </IonLabel>
            </IonSegmentButton>
          </IonSegment>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        <PageContainer>
          {activeTab === "all" && (
            <StyledCard>
              <CardContent>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    color: "#0066cc",
                    fontWeight: 700,
                    mb: 3,
                  }}
                >
                  <FilterList /> Advanced Filters
                </Typography>

                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Search Requests"
                      variant="outlined"
                      size="small"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search sx={{ color: "#0066cc" }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "&:hover fieldset": {
                            borderColor: "#0066cc",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#0066cc",
                          },
                        },
                        "& .MuiInputLabel-root.Mui-focused": {
                          color: "#0066cc",
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel sx={{ "&.Mui-focused": { color: "#0066cc" } }}>Department</InputLabel>
                      <Select
                        value={selectedDepartment}
                        label="Department"
                        onChange={(e) => setSelectedDepartment(e.target.value)}
                        sx={{
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#0066cc",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#0066cc",
                          },
                        }}
                      >
                        <MenuItem value="all">All Departments</MenuItem>
                        {departments.map((dept, i) => (
                          <MenuItem key={dept.department_id} value={dept.department_id}>
                            {dept.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel sx={{ "&.Mui-focused": { color: "#0066cc" } }}>Status</InputLabel>
                      <Select
                        value={statusFilter}
                        label="Status"
                        onChange={(e) => setStatusFilter(e.target.value)}
                        sx={{
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#0066cc",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#0066cc",
                          },
                        }}
                      >
                        {statusOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={2}>
                    <DatePicker
                      label="Start Date"
                      value={startDate}
                      onChange={(newValue) => setStartDate(newValue)}
                      slotProps={{
                        textField: {
                          size: "small",
                          fullWidth: true,
                          sx: {
                            "& .MuiOutlinedInput-root": {
                              "&:hover fieldset": {
                                borderColor: "#0066cc",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#0066cc",
                              },
                            },
                          },
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={2}>
                    <DatePicker
                      label="End Date"
                      value={endDate}
                      onChange={(newValue) => setEndDate(newValue)}
                      slotProps={{
                        textField: {
                          size: "small",
                          fullWidth: true,
                          sx: {
                            "& .MuiOutlinedInput-root": {
                              "&:hover fieldset": {
                                borderColor: "#0066cc",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#0066cc",
                              },
                            },
                          },
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={1}>
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={() => {
                        setSelectedDepartment("all")
                        setStatusFilter("all")
                        setStartDate(null)
                        setEndDate(null)
                        setSearchQuery("")
                      }}
                      size="small"
                      sx={{
                        borderColor: "#0066cc",
                        color: "#0066cc",
                        "&:hover": {
                          borderColor: "#0052a3",
                          backgroundColor: "#f0f7ff",
                        },
                      }}
                    >
                      Clear
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </StyledCard>
          )}

          {activeTab === "pending" && (
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={4}>
                <StatsCard>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Total Pending
                      </Typography>
                      <Typography variant="h4" fontWeight={700} color="#FFA726">
                        {filteredRequests.length}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        backgroundColor: "#FFF3E0",
                        borderRadius: "12px",
                        p: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Pending sx={{ fontSize: 40, color: "#FFA726" }} />
                    </Box>
                  </Box>
                </StatsCard>
              </Grid>
              <Grid item xs={12} md={4}>
                <StatsCard>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Total Items
                      </Typography>
                      <Typography variant="h4" fontWeight={700} color="#42A5F5">
                        {filteredRequests.reduce((sum, req) => sum + (req.items?.length || 0), 0)}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        backgroundColor: "#E3F2FD",
                        borderRadius: "12px",
                        p: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Info sx={{ fontSize: 40, color: "#42A5F5" }} />
                    </Box>
                  </Box>
                </StatsCard>
              </Grid>
              <Grid item xs={12} md={4}>
                <StatsCard>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  
                    <Box
                      sx={{
                        backgroundColor: "#E8F5E9",
                        borderRadius: "12px",
                        p: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <CheckCircle sx={{ fontSize: 40, color: "#66BB6A" }} />
                    </Box>
                  </Box>
                </StatsCard>
              </Grid>
            </Grid>
          )}

          <StyledCard>
            <CardContent>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                <Box>
                  <Typography variant="h5" component="h2" fontWeight={700} color="#0066cc">
                    {activeTab === "pending" ? "Pending Approval Queue" : "All Department Requests"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {activeTab === "pending"
                      ? "Review and approve pending inventory requests"
                      : "View all inventory requests and their status"}
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={fetchRequests}
                  disabled={loading}
                  sx={{
                    borderColor: "#0066cc",
                    color: "#0066cc",
                    fontWeight: 600,
                    "&:hover": {
                      borderColor: "#0052a3",
                      backgroundColor: "#f0f7ff",
                    },
                  }}
                >
                  Refresh
                </Button>
              </Box>

              <TableContainer
                component={Paper}
                sx={{
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
                  borderRadius: "8px",
                  border: "1px solid #e8edf2",
                }}
              >
                <Table>
                  <TableHead sx={{ backgroundColor: "#f5f8fa" }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, color: "#0066cc", fontSize: "0.875rem" }}>Request ID</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: "#0066cc", fontSize: "0.875rem" }}>Department</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: "#0066cc", fontSize: "0.875rem" }}>
                        Requested By
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, color: "#0066cc", fontSize: "0.875rem" }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: "#0066cc", fontSize: "0.875rem" }}>Items</TableCell>
                      
                      <TableCell sx={{ fontWeight: 700, color: "#0066cc", fontSize: "0.875rem" }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: "#0066cc", fontSize: "0.875rem" }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                          <CircularProgress sx={{ color: "#0066cc" }} />
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                            Loading requests...
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : filteredRequests.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                          <Alert
                            severity="info"
                            sx={{
                              display: "inline-flex",
                              backgroundColor: "#E3F2FD",
                              color: "#0066cc",
                              "& .MuiAlert-icon": {
                                color: "#0066cc",
                              },
                            }}
                          >
                            {activeTab === "pending"
                              ? "No pending requests at this time"
                              : "No requests found matching your filters"}
                          </Alert>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredRequests.map((request, i) => (
                        <TableRow
                          key={i}
                          hover
                          sx={{
                            "&:hover": {
                              backgroundColor: "#f5f8fa",
                            },
                            transition: "background-color 0.2s",
                          }}
                        >
                          <TableCell>
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              sx={{
                                color: "#0066cc",
                                fontFamily: "monospace",
                              }}
                            >
                              {request.reference}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={request.department_name}
                              size="small"
                              sx={{
                                backgroundColor: "#E3F2FD",
                                color: "#0066cc",
                                fontWeight: 600,
                                border: "1px solid #90CAF9",
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              {request.requested_by_name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {new Date(request.created_at).toLocaleDateString("en-US", {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              })}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                              <Typography variant="body2" fontWeight={600} color="#42A5F5">
                                {request.items?.length || 0}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                items
                              </Typography>
                            </Box>
                          </TableCell>
                         
                          <TableCell>{getStatusChip(request.status)}</TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.5}>
                              <Tooltip title="View Details" arrow>
                                <IconButton
                                  size="small"
                                  onClick={() => handleViewRequest(request)}
                                  sx={{
                                    color: "#42A5F5",
                                    "&:hover": {
                                      backgroundColor: "#E3F2FD",
                                    },
                                  }}
                                >
                                  <Visibility fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              {request.status === "pending" && (
                                <>
                                  <Tooltip title="Approve Request" arrow>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleApproveRequest(request.reference)}
                                      sx={{
                                        color: "#66BB6A",
                                        "&:hover": {
                                          backgroundColor: "#E8F5E9",
                                        },
                                      }}
                                    >
                                      <ThumbUp fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Reject Request" arrow>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleRejectRequest(request.reference)}
                                      sx={{
                                        color: "#EF5350",
                                        "&:hover": {
                                          backgroundColor: "#FFEBEE",
                                        },
                                      }}
                                    >
                                      <ThumbDown fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              )}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={totalRequests}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(Number.parseInt(e.target.value, 10))
                  setPage(0)
                }}
                sx={{
                  borderTop: "1px solid #e8edf2",
                  "& .MuiTablePagination-select": {
                    color: "#0066cc",
                  },
                  "& .MuiTablePagination-selectIcon": {
                    color: "#0066cc",
                  },
                }}
              />
            </CardContent>
          </StyledCard>
        </PageContainer>

        <Dialog
          open={showDetailDialog}
          onClose={() => setShowDetailDialog(false)}
          maxWidth="md"
          fullWidth
          scroll="paper"
          PaperProps={{
            sx: {
              borderRadius: "12px",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
            },
          }}
        >
          <DialogTitle sx={{ backgroundColor: "#0066cc", color: "#ffffff", pb: 3 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Box>
                <Typography variant="h6" fontWeight={700}>
                  Request Details
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9, fontFamily: "monospace" }}>
                  {selectedRequest?.reference}
                </Typography>
              </Box>
              {selectedRequest && getStatusChip(selectedRequest.status)}
            </Box>
          </DialogTitle>
          <DialogContent dividers sx={{ backgroundColor: "#f5f8fa" }}>
            {selectedRequest && (
              <>
                <Grid container spacing={3} sx={{ mb: 3, mt: 0.5 }}>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ backgroundColor: "#ffffff", p: 2, borderRadius: "8px", border: "1px solid #e8edf2" }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        DEPARTMENT
                      </Typography>
                      <Typography variant="body1" fontWeight={600} color="#0066cc" sx={{ mt: 0.5 }}>
                        {selectedRequest.department_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ID: {selectedRequest.department_id}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ backgroundColor: "#ffffff", p: 2, borderRadius: "8px", border: "1px solid #e8edf2" }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        REQUESTED BY
                      </Typography>
                      <Typography variant="body1" fontWeight={600} sx={{ mt: 0.5 }}>
                        {selectedRequest.requested_by_name}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ backgroundColor: "#ffffff", p: 2, borderRadius: "8px", border: "1px solid #e8edf2" }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        REQUEST DATE
                      </Typography>
                      <Typography variant="body1" fontWeight={500} sx={{ mt: 0.5 }}>
                        {new Date(selectedRequest.created_at).toLocaleString("en-US", {
                          month: "long",
                          day: "numeric",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </Typography>
                    </Box>
                  </Grid>
                  {/* <Grid item xs={12} md={6}>
                    <Box sx={{ backgroundColor: "#ffffff", p: 2, borderRadius: "8px", border: "1px solid #e8edf2" }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        TOTAL VALUE
                      </Typography>
                      <Typography variant="h6" fontWeight={700} color="#66BB6A" sx={{ mt: 0.5 }}>
                        ${Number.parseFloat(selectedRequest.total_value).toFixed(2)}
                      </Typography>
                    </Box>
                  </Grid> */}
                </Grid>

                {selectedRequest.notes && (
                  <Alert
                    severity="info"
                    icon={<Info />}
                    sx={{
                      mb: 3,
                      backgroundColor: "#E3F2FD",
                      borderLeft: "4px solid #42A5F5",
                      "& .MuiAlert-icon": {
                        color: "#42A5F5",
                      },
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight={600}>
                      Additional Notes:
                    </Typography>
                    <Typography variant="body2">{selectedRequest.notes}</Typography>
                  </Alert>
                )}

                <Typography variant="h6" gutterBottom fontWeight={700} color="#0066cc" sx={{ mb: 2 }}>
                  Requested Items
                </Typography>
                <TableContainer
                  component={Paper}
                  sx={{
                    mb: 3,
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
                    borderRadius: "8px",
                    border: "1px solid #e8edf2",
                  }}
                >
                  <Table size="small">
                    <TableHead sx={{ backgroundColor: "#f5f8fa" }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, color: "#0066cc" }}>Item Name</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: "#0066cc" }}>Item ID</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: "#0066cc" }}>
                          Requested
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: "#0066cc" }}>
                          Available
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: "#0066cc" }}>
                          Approve Qty
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedRequest.items.map((item) => (
                        <TableRow key={item.id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              {item.stock_name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={item.item_id}
                              size="small"
                              sx={{
                                backgroundColor: "#E3F2FD",
                                color: "#0066cc",
                                fontFamily: "monospace",
                                fontSize: "0.7rem",
                                border: "1px solid #90CAF9",
                              }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight={600} color="#FFA726">
                              {item.requested_qty}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight={500} color="text.secondary">
                              {item.available_quantity}
                            </Typography>
                          </TableCell>

                          <TableCell align="right">
                            {selectedRequest.status === "pending" ? (
                              <TextField
                                type="number"
                                size="small"
                                value={item.approved_qty !== undefined ? item.approved_qty : item.requested_qty}
                                onChange={(e) => handleUpdateItemQuantity(item.id, Number.parseInt(e.target.value))}
                                inputProps={{
                                  min: 0,
                                  max: Math.min(item.requested_qty, Number.parseInt(item.available_quantity)),
                                }}
                                sx={{
                                  width: 80,
                                  "& .MuiOutlinedInput-root": {
                                    "&:hover fieldset": {
                                      borderColor: "#0066cc",
                                    },
                                    "&.Mui-focused fieldset": {
                                      borderColor: "#0066cc",
                                    },
                                  },
                                }}
                              />
                            ) : (
                              <Typography variant="body2" fontWeight={600} color="#66BB6A">
                                {item.approved_qty !== undefined ? item.approved_qty : "-"}
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2.5, backgroundColor: "#f5f8fa" }}>
            <Button
              onClick={() => setShowDetailDialog(false)}
              sx={{
                color: "#757575",
                "&:hover": {
                  backgroundColor: "#e8edf2",
                },
              }}
            >
              Close
            </Button>
            {selectedRequest?.status === "pending" && (
              <>
                <Button
                  variant="outlined"
                  startIcon={<ThumbDown />}
                  onClick={() => {
                    setShowDetailDialog(false)
                    handleRejectRequest(selectedRequest.reference)
                  }}
                  sx={{
                    borderColor: "#EF5350",
                    color: "#EF5350",
                    "&:hover": {
                      borderColor: "#D32F2F",
                      backgroundColor: "#FFEBEE",
                    },
                  }}
                >
                  Reject
                </Button>
                <Button
                  variant="contained"
                  startIcon={<ThumbUp />}
                  onClick={() => {
                    setShowDetailDialog(false)
                    setSelectedAction("approve")
                    setShowApproveDialog(true)
                  }}
                  sx={{
                    backgroundColor: "#66BB6A",
                    "&:hover": {
                      backgroundColor: "#4CAF50",
                    },
                  }}
                >
                  Approve Request
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>

        <Dialog
          open={showApproveDialog}
          onClose={() => setShowApproveDialog(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: "12px",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
            },
          }}
        >
          <DialogTitle sx={{ backgroundColor: "#E8F5E9", borderBottom: "1px solid #C8E6C9" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  backgroundColor: "#66BB6A",
                  borderRadius: "50%",
                  p: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ThumbUp sx={{ color: "#ffffff" }} />
              </Box>
              <Typography variant="h6" fontWeight={700} color="#2E7D32">
                Confirm Approval
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ mt: 3 }}>
            <Typography variant="body1" gutterBottom fontWeight={500}>
              Are you sure you want to approve this request?
            </Typography>

            {selectedRequest && (
              <Box sx={{ mt: 3, p: 2.5, backgroundColor: "#f5f8fa", borderRadius: "8px", border: "1px solid #e8edf2" }}>
                <Typography variant="subtitle2" fontWeight={700} color="#0066cc" gutterBottom>
                  Request Summary
                </Typography>
                <Stack spacing={1} sx={{ mt: 1.5 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="body2" color="text.secondary">
                      Request ID:
                    </Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ fontFamily: "monospace" }}>
                      {selectedRequest.reference}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="body2" color="text.secondary">
                      Department:
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {selectedRequest.department_name}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="body2" color="text.secondary">
                      Total Items:
                    </Typography>
                    <Typography variant="body2" fontWeight={600} color="#42A5F5">
                      {selectedRequest.items.length}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="body2" color="text.secondary">
                      Total Value:
                    </Typography>
                    <Typography variant="body2" fontWeight={700} color="#66BB6A">
                      ${Number.parseFloat(selectedRequest.total_value).toFixed(2)}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2.5, backgroundColor: "#f5f8fa" }}>
            <Button
              onClick={() => setShowApproveDialog(false)}
              disabled={loading}
              sx={{
                color: "#757575",
                "&:hover": {
                  backgroundColor: "#e8edf2",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmitApproval}
              variant="contained"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} sx={{ color: "#ffffff" }} /> : <Check />}
              sx={{
                backgroundColor: "#66BB6A",
                px: 3,
                fontWeight: 600,
                "&:hover": {
                  backgroundColor: "#4CAF50",
                },
              }}
            >
              {loading ? "Approving..." : "Approve Request"}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={showRejectDialog}
          onClose={() => setShowRejectDialog(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: "12px",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
            },
          }}
        >
          <DialogTitle sx={{ backgroundColor: "#FFEBEE", borderBottom: "1px solid #FFCDD2" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  backgroundColor: "#EF5350",
                  borderRadius: "50%",
                  p: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ThumbDown sx={{ color: "#ffffff" }} />
              </Box>
              <Typography variant="h6" fontWeight={700} color="#C62828">
                Confirm Rejection
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ mt: 3 }}>
            <Typography variant="body1" gutterBottom fontWeight={500}>
              Please provide a reason for rejecting this request:
            </Typography>

            <TextField
              fullWidth
              multiline
              rows={4}
              label="Rejection Reason"
              placeholder="Enter the reason for rejection (required for audit purposes)..."
              sx={{
                mt: 2,
                "& .MuiOutlinedInput-root": {
                  "&:hover fieldset": {
                    borderColor: "#EF5350",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#EF5350",
                  },
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "#EF5350",
                },
              }}
              id="rejection-reason"
            />
          </DialogContent>
          <DialogActions sx={{ p: 2.5, backgroundColor: "#f5f8fa" }}>
            <Button
              onClick={() => setShowRejectDialog(false)}
              disabled={loading}
              sx={{
                color: "#757575",
                "&:hover": {
                  backgroundColor: "#e8edf2",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                const reasonInput = document.getElementById("rejection-reason") as HTMLInputElement
                const reason = reasonInput?.value.trim()
                if (!reason) {
                  showErrorToast("Rejection reason is required.")
                  return
                }
                handleSubmitRejection(reason)
              }}
              variant="contained"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} sx={{ color: "#ffffff" }} /> : <Close />}
              sx={{
                backgroundColor: "#EF5350",
                px: 3,
                fontWeight: 600,
                "&:hover": {
                  backgroundColor: "#D32F2F",
                },
              }}
            >
              {loading ? "Rejecting..." : "Reject Request"}
            </Button>
          </DialogActions>
        </Dialog>

        <IonLoading isOpen={loading} message={"Processing..."} />
        <IonToast
          isOpen={showToast}
          onDidDismiss={() => setShowToast(false)}
          message={toastMessage}
          duration={3000}
          color={toastType}
        />
      </IonContent>
    </IonPage>
  )
}

export default ItemTransferPage
